/**
 * karmabooking-api
 *
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

/**
 * The working plural config for this instance.
 *
 * Yes, this a global singleton. But that's okay. It's for SPA apps. The
 * translations and configs are stored globally on window too.
 */
let PLURALS = new Intl.PluralRules(window.__translations.lang);


/**
 * Translate this message.
 *
 * Given the first number argument, this will find plural forms for the given
 * format/id string.
 *
 * @param text a c-style(-ish) format/id string
 * @param args var-args of interpolations
 */
export function tr(text: string, ...args: any[]): string {
    const id = text.trim().toLowerCase();

    // Find the first number in the args, this will serve as our 'plural'
    // identifier.
    const index = args.find(arg => typeof arg === "number") as number | undefined;

    // Find the appropriate translated text.
    // If not found - return the original.
    // If not found and [annotated] - return nothing.
    const alt = lookupTr(id, index) ?? (/^\[[^\]]+\]$/.test(text) ? '' : text);

    // Interpolate the arguments.
    let i = 0;
    return alt.replace(/%[ds]/g, m => args[i++]);
}


/**
 * Get an appropriate translation for this message.
 * This may be a msgid or msgid_plural.
 *
 * Provide an 'index' to retrieve the appropriate plural form.
 *
 * Note: '__translations' must exist (should be injected by Craft).
 *
 * @param id
 * @param plural
 */
function lookupTr(id: string, plural?: number): string | undefined {
    const lookup = window.__translations.tr[id] as undefined | string | string[];

    // Strings are plural->id references, arrays[] are translations.
    const text = typeof lookup === "string"
        ? window.__translations.tr[lookup] as undefined | string[]
        : lookup;

    if (typeof text === 'undefined') {
        return undefined;
    }
    if (typeof plural === 'undefined') {
        return text[0];
    }
    else {
        const pluralRule = PLURALS.select(plural);
        const pluralIndex = window.__translations.plurals[pluralRule] ?? 0;
        return text[pluralIndex];
    }
}


/**
 * Get a new set of translations for this language.
 * The fallback is 'en' if the language isn't provided.
 */
export async function loadLang(lang: string): Promise<string> {
    const { prefix } = window.__config;
    const res = await fetch(`/${prefix}/translation.json?lang=${lang}&persist`);
    const json = await res.json();

    window.__translations = json;
    PLURALS = new Intl.PluralRules(json.lang);
    return json.lang;
}
